import axios from 'axios'
import { toast } from 'react-toastify'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { hideLoading, showLoading } from 'react-redux-loading-bar'
import { useOutletContext } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import StudentsTable from '././components/StudentsTable'
import MembersSearchInput from './components/MembersSearchInput'
import DetailsDrawer from './components/DetailsDrawer'
import TablePlaceholder from './components/TablePlaceholder'
// import FilterButton from './components/FilterButton'

import ColumnManagementPopover from './components/ColumnManagementPopover'
import FiltersPopover from './components/FiltersPopover'
import { ReactComponent as DownloadIcon } from '../../assets/images/common/icons/download-icon.svg'
import { ReactComponent as FilterIcon } from '../../assets/images/common/icons/filter-icon.svg'
import { ReactComponent as PanelIcon } from '../../assets/images/common/icons/panel-icon.svg'
import { ReactComponent as ArrowDown } from '../../assets/images/common/icons/arrowDown.svg'

import classes from './StudentsPage.module.scss'
import colors from '../../assets/styles/globals.scss'

import useReportPolling from '../dashboard/coachDashboard/useReportPolling'
import { deserialize } from '../../utility/serialization'
import { debounce } from '../../utility/debounceHelper'
import { requestGetAllStudents } from '../../actions/communitiesActions'
import { requestExportCommunityStudents } from '../../actions/reportsActions'
import ExportMembersModal from './components/ExportMembersModal'
import { PER_PAGE_DEFAULT } from '../../misc/Constants'
import { DEFAULT_SORT_OPTION } from '../../utility/members'
import useColumnManagement from './hooks/useColumnManagement'
import useStudentsFilters, { buildApiFilters } from './hooks/useStudentsFilters'


const StudentsPage = () => {
  const dispatch = useDispatch()
  const { loadedStudentsData } = useOutletContext()
  const abortController = useRef(null)
  const communityId = useSelector(state => state.user.current_user.communities[0].id)
  const [students, setStudents] = useState(loadedStudentsData?.students)
  const [pagination, setPagination] = useState(loadedStudentsData?.meta.pagination)
  const [searchText, setSearchText] = useState('')
  const [sortOption, setSortOption] = useState(DEFAULT_SORT_OPTION)
  const [isLoading, setIsLoading] = useState(false)
  const [isSortLoading, setIsSortLoading] = useState(false)
  const [inspectedStudent, setInspectedStudent] = useState(null)

  const { visibleColumns, allColumns, hiddenColumns, isDefaultOrder, toggleColumn, reorderColumns, resetToDefaults } = useColumnManagement()
  const { filters, apiFilters, activeFilterCount, applyFilters, INITIAL_FILTERS } = useStudentsFilters()
  const [isColumnPopoverOpen, setIsColumnPopoverOpen] = useState(false)
  const [isFiltersPopoverOpen, setIsFiltersPopoverOpen] = useState(false)

  const [exportId, setExportId] = useState(null)
  const [exportUrl, exportError, clearReportPolling] = useReportPolling({ exportId, setExportId })

  const isExporting = !!(exportId)

  const fetchStudents = useCallback(
    ({ page, search, sortField, sortValue, filters, append }) =>
      requestGetAllStudents({
        communityId,
        page,
        perPage: PER_PAGE_DEFAULT,
        search,
        userIds: null,
        sortField,
        sortValue,
        status: 'active',
        filters,
        signal: abortController.current?.signal,
      })
        .then(response => {
          const data = deserialize(response.data)
          setStudents(prev => {
            if (append) { return [...prev, ...data.students] }
            return data.students
          })
          setPagination(data.meta.pagination)
        }),
    [communityId]
  )

  const updateStudents = useCallback(
    (params) => {
      setIsLoading(true)
      fetchStudents({
        page: 1,
        append: false,
        sortField: sortOption.sortField,
        sortValue: sortOption.sortValue,
        filters: apiFilters,
        ...params,
      })
        .catch((res) => {
          if (!axios.isCancel(res)) {
            toast.error('Something went wrong. Please try again.', { position: toast.POSITION.TOP_RIGHT })
          }
        })
        .finally(() => { setIsLoading(false); setIsSortLoading(false) })
    },
    [fetchStudents, sortOption.sortField, sortOption.sortValue, apiFilters]
  )

  const fetchMoreStudents = () => {
    updateStudents({ page: pagination?.page + 1, append: true, search: searchText })
  }

  const debouncedSearch = useMemo(
    () => debounce((search) => updateStudents({ search })),
    [updateStudents]
  )

  const handleSearch = (e) => {
    setSearchText(e.target.value)
    debouncedSearch(e.target.value)
  }

  const handleSorting = useCallback((sortField, sortValue) => {
    setIsSortLoading(true)
    setSortOption({ sortField, sortValue })
    updateStudents({ sortField, sortValue, search: searchText })
  }, [setSortOption, updateStudents, searchText])

  const openViewDetails = (student) => {
    setInspectedStudent(student)
  }

  const handleApplyFilters = useCallback((newFilters) => {
    applyFilters(newFilters)
    updateStudents({ filters: buildApiFilters(newFilters), search: searchText })
  }, [applyFilters, updateStudents, searchText])

  const handleExport = () => {
    dispatch(showLoading())
    const columnOrder = isDefaultOrder ? undefined : visibleColumns.map(c => c.exportKey)
    requestExportCommunityStudents({ filters: apiFilters, columnOrder })
      .then((response) => {
        setExportId(deserialize(response.data).jobId)
      })
      .catch(() => toast.error('Something went wrong. Please try again.', { position: toast.POSITION.TOP_RIGHT }))
  }

  const clearExportResult = () => {
    dispatch(hideLoading())
    clearReportPolling()
  }

  useEffect(() => {
    if (exportUrl) {
      dispatch(hideLoading())
    }
  }, [exportUrl, exportError, dispatch])

  return (
    <main className={classes.wrapper}>
      <header className='py-3 d-flex align-items-center'>
        <div className={classes.searchWrapper}>
          <MembersSearchInput placeholder='Search for Students' value={searchText} onChange={handleSearch} />
          <button
            id='filtersButton'
            onClick={() => setIsFiltersPopoverOpen(prev => !prev)}
            className={classes.filterButton}
          >
            <FilterIcon color={activeFilterCount > 0 ? colors.primaryOrange : colors.darkNavy} width={18} height={18} />
            {activeFilterCount > 0
              ? <span className={classes.filterActiveLabel}>{activeFilterCount} {activeFilterCount === 1 ? 'Filter' : 'Filters'} Applied</span>
              : 'Filters'
            }
          </button>
        </div>
        <div className={classes.buttonsSection}>
          <button
            id='exportButton'
            onClick={handleExport}
            className={classes.exportButton}
            disabled={isExporting}
            aria-label='Export Members CSV'
          >
            Export <DownloadIcon color={colors.darkNavy} width={16} height={16} />
          </button>
          <button
            id='columnManagementButton'
            onClick={() => setIsColumnPopoverOpen(prev => !prev)}
            className={classes.panelButton}
          >
            <PanelIcon color={colors.darkNavy} width={18} height={18} />
            <ArrowDown width={14} height={14} className={isColumnPopoverOpen ? classes.arrowUp : ''} />
          </button>
        </div>
      </header>
      {students?.length === 0 ? (
        <TablePlaceholder message='No Students' />
      ) : (
        <StudentsTable
          students={students}
          fetchData={fetchMoreStudents}
          onStudentClick={openViewDetails}
          pagination={pagination}
          inspectedStudentId={inspectedStudent?.id}
          isExporting={isExporting}
          isLoading={isLoading}
          isSortLoading={isSortLoading}
          sortOption={sortOption}
          applySort={handleSorting}
          visibleColumns={visibleColumns}
        />
      )}
      {!!inspectedStudent && (
        <DetailsDrawer student={inspectedStudent} onClose={() => setInspectedStudent(null)} />
      )}
      {(exportUrl || exportError) && (
        <ExportMembersModal exportUrl={exportUrl} exportError={exportError} clearExportResult={clearExportResult} />
      )}
      <ColumnManagementPopover
        isOpen={isColumnPopoverOpen}
        toggle={() => setIsColumnPopoverOpen(false)}
        allColumns={allColumns}
        hiddenColumns={hiddenColumns}
        toggleColumn={toggleColumn}
        reorderColumns={reorderColumns}
        resetToDefaults={resetToDefaults}
      />
      <FiltersPopover
        isOpen={isFiltersPopoverOpen}
        toggle={() => setIsFiltersPopoverOpen(prev => !prev)}
        currentFilters={filters}
        initialFilters={INITIAL_FILTERS}
        onApply={handleApplyFilters}
      />
    </main>
  )
}

export default StudentsPage
